import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import { ICustomSeriesPaneRenderer, PaneRendererCustomData, PriceToCoordinateConverter, PrimitivePaneViewZOrder } from 'lightweight-charts'
import { PlusIconData } from './data'
import { positionsBox, positionsLine } from './positions'
import { PlusIconSeriesOptions } from './options'

export class PlusIconSeriesRenderer<TData extends PlusIconData>
    implements ICustomSeriesPaneRenderer {
    _data: PaneRendererCustomData<number, TData> | null = null
    _options: PlusIconSeriesOptions | null = null

    draw(
        target: CanvasRenderingTarget2D,
        priceConverter: PriceToCoordinateConverter
    ): void {
        target.useBitmapCoordinateSpace(scope =>
            this._drawImpl(scope, priceConverter)
        )
    }

    update(
        data: PaneRendererCustomData<number, TData>,
        options: PlusIconSeriesOptions
    ): void {
        this._data = data
        this._options = options
    }


    _drawImpl(
        renderingScope: BitmapCoordinatesRenderingScope,
        priceToCoordinate: PriceToCoordinateConverter,
    ): void {
        if (
            this._data === null ||
            this._data.bars.length === 0 ||
            this._data.visibleRange === null ||
            this._options === null
        ) {
            return
        }
        const zeroY = priceToCoordinate(0) ?? 0

        const xPos = positionsLine(
            this._options?.xPosition ?? 0,
            renderingScope.horizontalPixelRatio,
            1
        )
        const yPos = positionsBox(
            zeroY,
            renderingScope.mediaSize.height,
            renderingScope.verticalPixelRatio
        )
        const squareWidth = 20 * renderingScope.horizontalPixelRatio
        const radius = 3 * renderingScope.horizontalPixelRatio
        const plusIconWidth = 1.5 * renderingScope.horizontalPixelRatio
        drawRoundedSquare(renderingScope.context, xPos.position - (squareWidth / 2), yPos.position - squareWidth, squareWidth, radius)
        drawPlusIcon(renderingScope.context, xPos.position - (squareWidth / 2), yPos.position - squareWidth, squareWidth, squareWidth / 2, plusIconWidth, 'white')
    }

}

function drawRoundedSquare(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, radius: number) {
    ctx.beginPath();

    // Top-left corner (rounded)
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + size - radius, y);
    ctx.arcTo(x + size, y, x + size, y + radius, radius);

    // Right side and bottom-right (no radius)
    ctx.lineTo(x + size, y + size);

    // Bottom side and bottom-left (no radius)
    ctx.lineTo(x, y + size);

    // Left side up to top-left (rounded)
    ctx.lineTo(x, y + radius);
    ctx.arcTo(x, y, x + radius, y, radius);

    ctx.closePath();

    ctx.fillStyle = 'black';
    ctx.fill();
}

function drawPlusIcon(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, lineLength = 20, lineWidth = 2, color = 'black') {
    const centerX = x + size / 2;
    const centerY = y + size / 2;

    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;

    // Draw circle around the plus
    const circlePadding = 5; // adjust as needed to fit around the plus
    const radius = lineLength / 2 + circlePadding;
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.stroke();

    // Draw plus sign
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;

    // Vertical line
    ctx.moveTo(centerX, centerY - lineLength / 2);
    ctx.lineTo(centerX, centerY + lineLength / 2);

    // Horizontal line
    ctx.moveTo(centerX - lineLength / 2, centerY);
    ctx.lineTo(centerX + lineLength / 2, centerY);

    ctx.stroke();
}